import { css } from '@emotion/core';
import {
  colorsPalette,
  hexToRgba,
  shadows,
} from '../../../../../styles/colors';
import { MediaQueries } from '../../../../../styles/Grid';
import { typography, fontWeights } from '../../../../../styles/typography';
import { labelLeftPadding } from '../../../../ui/Form/Checkbox/index.styles';

const MODAL_SPACE = 120;
const CTAS_HEIGHT = 104;

export const defaultClearButton = {
  color: colorsPalette.grey60,
  pointerEvents: 'none',
  position: 'relative',
  '&:after': {
    borderBottom: `1px solid ${colorsPalette.grey20}`,
    bottom: -2,
    content: "''",
    left: 0,
    position: 'absolute',
    width: '100%',
  },
};

export const collapsible = {
  '.Collapsible': {
    marginBottom: 40,
    '.Collapsible__contentOuter': {
      borderBottom: `1px solid ${colorsPalette.grey20}`,
      marginTop: 32,
    },
    '.Collapsible__trigger': {
      ...typography.effra18,
      background: 'transparent',
      border: 'none',
      fontWeight: fontWeights.medium,
      padding: 0,
      position: 'relative',
      textAlign: 'left',
      width: '100%',
      '&:after': {
        content: "'\\e916'", // icon-chevron
        fontFamily: 'icomoon',
        fontSize: 14,
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%) rotate(180deg)',
        transition: 'transform 250ms',
      },
      '&.is-closed': {
        '&:after': {
          transform: 'translateY(-50%)',
        },
      },
    },
    // A11y: prevents tabbing into content of closed collapse items
    '.Collapsible__trigger.is-closed + .Collapsible__contentOuter .Collapsible__contentInner': {
      visibility: 'hidden',
    },
  },
};

export const wrapper = css({
  ...collapsible,
  maxHeight: `calc(100% - ${CTAS_HEIGHT}px)`,
  overflow: 'auto',
  padding: '64px 24px 40px',
  [MediaQueries.md]: {
    padding: '40px 40px 32px',
  },
  [MediaQueries.lg]: {
    padding: '80px 75px 40px',
  },
  [MediaQueries.xl]: {
    padding: '80px 109px 40px',
  },
});

export const brandWrapper = css({
  marginBottom: 32,
});

export const title = css(typography.effra58Modal, {
  marginBottom: 72,
  [MediaQueries.lgAndUp]: {
    ...typography.effra40,
    fontWeight: fontWeights.medium,
  },
});

export const description = css(typography.effra14, {
  color: hexToRgba(colorsPalette.black, 0.5),
  paddingLeft: labelLeftPadding,
});

export const checkboxLabel = css({
  '&:not(:last-child) label': {
    marginBottom: 9,
  },
});

export const number = css(typography.effra12Link, {
  marginLeft: 8,
  backgroundColor: colorsPalette.grey10,
  padding: '2px 5px',
  fontWeight: fontWeights.medium,
});

export const modalOverlay = {
  background: hexToRgba(colorsPalette.black, 0.9),
};

const modalLgAndXl = {
  bottom: `${MODAL_SPACE}px !important`,
  height: `calc(100vh - ${MODAL_SPACE * 2}px)`,
  margin: '0 auto',
  position: 'relative',
  top: `${MODAL_SPACE}px !important`,
};

export const modalContent = css({
  height: '100vh',
  [MediaQueries.lg]: {
    ...modalLgAndXl,
    maxWidth: 868,
  },
  [MediaQueries.xl]: {
    ...modalLgAndXl,
    maxWidth: 890,
  },
});

export const closeModalButton = css({
  background: colorsPalette.white,
  boxShadow: `0px 0px 10px 17px ${colorsPalette.white}`,
  [MediaQueries.lgAndUp]: {
    right: 32,
    top: 32,
  },
});

export const ctas = css({
  alignItems: 'center',
  background: colorsPalette.white,
  bottom: 0,
  boxShadow: shadows.shadow80,
  display: 'flex',
  left: 0,
  minHeight: CTAS_HEIGHT,
  padding: 24,
  position: 'absolute',
  width: '100%',
  [MediaQueries.mdAndUp]: {
    paddingLeft: 40,
    paddingRight: 40,
  },
  [MediaQueries.lg]: {
    paddingLeft: 75,
    paddingRight: 75,
  },
});

export const resultsButton = css({
  height: 'auto',
  padding: '20px 24px',
  width: '50%',
  [MediaQueries.mdAndUp]: {
    maxWidth: 240,
  },
});

export const resetButtonWrapper = css({
  width: '50%',
  [MediaQueries.mdAndUp]: {
    display: 'inline-block',
    marginLeft: 32,
    width: 'auto',
  },
});

export const resetButton = css(typography.effra16, {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  display: 'block',
  margin: '0 auto',
  padding: 16,
});

export const resetText = css({
  ...defaultClearButton,
});

export const sortItemButton = css({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  marginBottom: 24,
  width: '100%',
  '&:last-child': {
    marginBottom: 32,
  },
});

export const sortText = isActive =>
  css(typography.effra18, {
    color: isActive && colorsPalette.teal,
    paddingLeft: !isActive && 23,
    pointerEvents: 'none',
  });

export const checkIcon = css({
  marginRight: 10,
  fontSize: 10,
  color: colorsPalette.teal,
});

export const inputsContainer = css({
  display: 'flex',
  alignItems: 'center',
});

export const inputs = css({
  alignItems: 'center',
  display: 'flex',
});

export const rangeTextTo = css(typography.effra18, {
  marginLeft: 4,
  marginRight: 4,
  paddingBottom: 4,
  [MediaQueries.mdAndUp]: {
    marginLeft: 8,
    marginRight: 8,
  },
});

export const rangeTextPer = css(typography.effra18, {
  marginLeft: 24,
  paddingBottom: 4,
  width: '45%',
  [MediaQueries.mdAndUp]: {
    marginLeft: 8,
    width: 'auto',
  },
});

export const rangeInfo = css(typography.effra16, {
  color: colorsPalette.grey60,
  marginBottom: 32,
  marginTop: 24,
});

export const inputNumber = css({
  maxWidth: 160,
  [MediaQueries.lgAndUp]: {
    maxWidth: 185,
  },
});

export const inputWidth = css({
  width: '100%',
});
